import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, User } from 'lucide-react';
import { listarCongelados } from '../../../data/estoque';
import { listarSemenDoRelatorio } from '../../../data/semen';
import { clientesComEstoque } from '../../../utils/calculations/extratoDeEstoque';
import EstoqueDoCliente from './EstoqueDoCliente';

/**
 * O BOTIJÃO VISTO POR DONO.
 *
 * As outras duas visões do Estoque são por MATERIAL: uma lista embrião, a outra
 * lista sêmen, e cada uma tem o seu jeito de agrupar. Nenhuma das duas responde
 * "o que é do fulano", que é a pergunta que chega por telefone — quem atendia
 * tinha que abrir as duas e cruzar os nomes de cabeça.
 *
 * A visão por cliente junta os dois materiais num nome só, e abre no detalhe que
 * a linha do cliente na carteira também abre. O mesmo painel nos dois lugares de
 * propósito: é a mesma pergunta, feita de dois caminhos, e duas telas parecidas
 * com números que podem divergir é o que se está evitando.
 *
 * DUAS CONSULTAS PARA A LISTA, e as do detalhe só quando um nome abre. Buscar o
 * extrato de todos para mostrar uma tabela de saldos seria pagar caro por uma
 * pergunta que ninguém fez ainda.
 */

const num = (v) => Number(v || 0).toLocaleString('pt-BR');

const doses = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
};

const EstoquePorCliente = ({ currentLab, clients = [] }) => {
  const [congelados, setCongelados] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(null);

  useEffect(() => {
    if (!currentLab?.id) return undefined;
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    Promise.all([
      listarCongelados(currentLab.id),
      listarSemenDoRelatorio({ laboratoryId: currentLab.id }),
    ])
      .then(([c, p]) => {
        if (cancelado) return;
        setCongelados(c || []);
        setPartidas(p || []);
      })
      .catch(e => {
        console.error('Estoque por cliente:', e);
        // Sem cópia local: saldo velho apresentado como atual faz alguém prometer
        // ao telefone uma palheta que já saiu.
        if (!cancelado) setErro(e.message);
      })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
  }, [currentLab?.id]);

  const linhas = useMemo(
    () => clientesComEstoque(congelados, partidas, clients),
    [congelados, partidas, clients]);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return linhas;
    return linhas.filter(l => (l.client_name || '').toLowerCase().includes(q));
  }, [linhas, busca]);

  if (carregando) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-6 py-10 text-center">
        <p className="text-sm text-slate-500">Carregando o estoque…</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-6 py-8 text-center">
        <p className="text-sm text-slate-600">Não foi possível ler o estoque: {erro}</p>
      </div>
    );
  }

  if (linhas.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-6 py-10 text-center">
        <User className="mx-auto mb-3 text-slate-300" size={32} />
        <p className="text-sm text-slate-600">Nenhum cliente com material guardado.</p>
      </div>
    );
  }

  const totalEmbrioes = linhas.reduce((t, l) => t + l.embrioes, 0);
  const totalDoses = linhas.reduce((t, l) => t + l.doses, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[14rem] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar cliente"
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <p className="text-xs text-slate-500 tabular-nums">
          {num(linhas.length)} cliente(s) · {num(totalEmbrioes)} {totalEmbrioes === 1 ? 'embrião' : 'embriões'}
          {totalDoses > 0 && <> · {doses(totalDoses)} dose{totalDoses === 1 ? '' : 's'}</>}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-700">Cliente</th>
              <th className="text-right px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Embriões</th>
              <th className="text-right px-3 py-2 font-medium text-slate-700 whitespace-nowrap">Doses</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visiveis.map(l => {
              const chave = l.client_id || l.client_name;
              const expandido = aberto === chave;
              // Sem id não há como buscar o detalhe: material antigo, de um
              // cliente que saiu da carteira. A linha conta o que existe e não
              // promete uma tela que não abriria.
              const temDetalhe = !!l.client_id;

              return (
                <React.Fragment key={chave}>
                  <tr className={expandido ? 'bg-slate-50' : 'hover:bg-slate-50 transition-colors'}>
                    <td className="px-3 py-2 font-medium text-slate-900">{l.client_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {l.embrioes > 0
                        ? <span className="font-semibold text-slate-900">{num(l.embrioes)}</span>
                        : <span className="text-slate-300">0</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {l.doses > 0
                        ? <span className="font-semibold text-slate-900">{doses(l.doses)}</span>
                        : <span className="text-slate-300">0</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {temDetalhe && (
                        <button
                          onClick={() => setAberto(a => (a === chave ? null : chave))}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                          title="Ver o detalhe e gerar o extrato em PDF"
                        >
                          {expandido ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          Abrir
                        </button>
                      )}
                    </td>
                  </tr>

                  {expandido && (
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="px-3 pb-3">
                        <EstoqueDoCliente
                          client={clients.find(c => c.id === l.client_id)
                            || { id: l.client_id, name: l.client_name }}
                          laboratoryId={currentLab?.id}
                          laboratorio={currentLab}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {visiveis.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">
                  Nenhum cliente com esse nome tem material guardado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EstoquePorCliente;
